import type Konva from "konva";
import { Arrow } from "konva/lib/shapes/Arrow";
import { Circle } from "konva/lib/shapes/Circle";
import { Ellipse } from "konva/lib/shapes/Ellipse";
import { Line } from "konva/lib/shapes/Line";
import { Path } from "konva/lib/shapes/Path";
import { Rect } from "konva/lib/shapes/Rect";
import { Text } from "konva/lib/shapes/Text";
import { Transformer } from "konva/lib/shapes/Transformer";

import { BASE_TELESTRATION_STAGE_SIZE } from "~/telestrations/constants/baseTelestrationStageSize";
import { applyInverseScale, getScaledAndRotatedCoordinates } from "~/telestrations/helpers/coordinateTransforms";
import { getScaledPointCoord } from "~/telestrations/helpers/pointCoordinateHelper";
import { rotatePoint } from "~/telestrations/helpers/spotlightShapeConfig";
import { ShapeType } from "~/telestrations/types/canvas";

import { SpotlightShapeName } from "../stores/telestrationStore";

interface CommonShapeAttributes {
  x: number;
  y: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
  dash?: string;
}

interface StageToSVGConversionResult {
  svgElement: string;
  maskElement?: string;
}

// arrowhead template path (tip at 0,0 pointing along +X)
const ARROWHEAD_TEMPLATE_PATH_D = "M 0 0 L -10 5 L -10 -5 Z";

/**
 * Gets compensated stroke width accounting for shape scale
 */
function getCompensatedStrokeWidth(shape: Konva.Shape): number {
  const avgScale = (shape.scaleX() + shape.scaleY()) / 2;
  return "strokeWidth" in shape ? shape.strokeWidth() / avgScale : 1;
}

/**
 * Gets common shape attributes (fill, stroke, strokeWidth, dash, x, y)
 */
function getCommonShapeAttributes(shape: Konva.Shape): CommonShapeAttributes {
  const strokeWidth = getCompensatedStrokeWidth(shape);
  const { x, y } = getScaledAndRotatedCoordinates(shape);
  return {
    x,
    y,
    fill: (shape.fill() as string) || "transparent",
    stroke: (shape.stroke() as string) || "black",
    strokeWidth,
    dash: shape.dash?.()?.join(",") || "none",
  };
}

/**
 * Builds SVG transform string for a shape
 */
function buildTransformString(shape: Konva.Shape): string {
  const rotation = shape instanceof Ellipse && shape.attrs.name === SpotlightShapeName.ELLIPSE ? 0 : shape.rotation();
  const rotationCenter = shape instanceof Line || shape instanceof Arrow ? { x: shape.x(), y: shape.y() } : undefined;
  const scaleX = shape.scaleX();
  const scaleY = shape.scaleY();

  if (rotationCenter) {
    return `transform="rotate(${rotation}, ${rotationCenter.x}, ${rotationCenter.y}) scale(${scaleX}, ${scaleY})"`;
  }

  return `transform="rotate(${rotation}) scale(${scaleX}, ${scaleY})"`;
}

/**
 * Checks if a shape is a spotlight shape
 */
function isSpotlightShape(shape: Konva.Shape): boolean {
  return shape.attrs.type === ShapeType.Spotlight;
}

// ======================
// Shape Converters
// ======================

/**
 * Converts a Konva Path to SVG path element
 */
function convertPathToSVG(shape: Path, transformStr: string, { fill, stroke, strokeWidth }: CommonShapeAttributes): StageToSVGConversionResult {
  const svgElement = `<path ${transformStr} id="${shape.id()}" d="${shape.data()}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" />`;

  return { svgElement };
}

/**
 * Converts a Konva Ellipse to SVG ellipse element
 */
function convertEllipseToSVG(
  shape: Ellipse,
  transformStr: string,
  { x, y, fill, stroke, strokeWidth, dash }: CommonShapeAttributes
): StageToSVGConversionResult {
  if (isSpotlightShape(shape)) {
    const maskElement = `<ellipse id="${shape.id()}" cx="${x}" cy="${y}" rx="${shape.radiusX()}" ry="${shape.radiusY()}" fill="black" ${transformStr} />`;
    return { svgElement: "", maskElement };
  }

  const svgElement = `<ellipse stroke-dasharray="${dash}" ${transformStr} id="${shape.id()}" cx="${x}" cy="${y}" rx="${shape.radiusX()}" ry="${shape.radiusY()}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" />`;

  return { svgElement };
}

/**
 * Converts a Konva Circle to SVG circle element
 */
function convertCircleToSVG(shape: Circle, transformStr: string, { fill, stroke, strokeWidth, dash }: CommonShapeAttributes): StageToSVGConversionResult {
  const svgElement = `<circle stroke-dasharray="${dash}" ${transformStr} id="${shape.id()}" cx="${shape.x() + shape.radius()}" cy="${shape.y() + shape.radius()}" r="${shape.radius()}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" />`;

  return { svgElement };
}

/**
 * Converts a Konva Rect to SVG rect element
 */
function convertRectToSVG(shape: Rect, transformStr: string, { x, y, fill, stroke, strokeWidth, dash }: CommonShapeAttributes): StageToSVGConversionResult {
  if (isSpotlightShape(shape)) {
    const firstPoint = rotatePoint({ x: shape.width() / 2, y: shape.height() / 2 }, shape.rotation());
    const centerX = firstPoint.x + shape.x();
    const centerY = firstPoint.y + shape.y();
    const transformOrigin = shape.rotation() ? `transform-origin="${centerX} ${centerY}"` : "";

    const maskElement =
      shape.attrs.name === SpotlightShapeName.ELLIPSE
        ? `<circle id="${shape.id()}" cx="${centerX}" cy="${centerY}" r="${Math.min(shape.width(), shape.height()) / 2}" fill="black" ${transformStr} ${transformOrigin} x="${shape.x()}" y="${shape.y()}" width="${shape.width()}" height="${shape.height()}" />`
        : `<rect id="${shape.id()}" x="${x}" y="${y}" width="${shape.width()}" height="${shape.height()}" fill="black" ${transformStr} ${transformOrigin} />`;

    return { svgElement: "", maskElement };
  }

  const svgElement = `<rect stroke-dasharray="${dash}" ${transformStr} id="${shape.id()}" x="${x}" y="${y}" width="${shape.width()}" height="${shape.height()}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" />`;

  return { svgElement };
}

/**
 * Converts a Konva Text to SVG text element (with optional background)
 */
function convertTextToSVG(shape: Text): StageToSVGConversionResult {
  const background = shape.attrs.background;
  const scaleX = shape.scaleX() || 1;
  const scaleY = shape.scaleY() || 1;
  const rotation = shape.rotation() || 0;
  const { x, y } = applyInverseScale({ x: shape.x(), y: shape.y(), rotation, scaleX, scaleY });
  const fontSize = shape.fontSize();

  const transformStr = `transform="scale(${scaleX}, ${scaleY}) rotate(${rotation}, ${x}, ${y})"`;

  let svgElement = "";

  // Add background rectangle if specified
  if (background !== undefined && background !== "transparent") {
    const width = shape.width() || 100;
    const height = shape.height() || fontSize * 1.2;
    const approxTextHeight = fontSize * 0.8;
    const totalPadding = height - approxTextHeight;
    const offsetY = totalPadding / 2;

    const backgroundY = y - offsetY;
    const backgroundHeight = height + offsetY;

    svgElement += `<rect data-text-background="true" ${transformStr}
      x="${x}" y="${backgroundY}"
      width="${width}" height="${backgroundHeight}"
      fill="${background}" />`;
  }

  // Add text element
  svgElement += `<text ${transformStr} id="${shape.id()}" x="${x}" y="${y}"
    font-size="${fontSize}"
    font-family="${shape.fontFamily()}"
    fill="${shape.fill() || "black"}"
    dominant-baseline="hanging"
    text-anchor="${shape.align()}">${shape.text()}</text>`;

  return { svgElement };
}

/**
 * Converts a Konva Line to SVG polyline/polygon element
 */
function convertLineToSVG(shape: Line, transformStr: string, { fill, stroke, strokeWidth }: CommonShapeAttributes): StageToSVGConversionResult {
  const points = shape.points();
  const lineX = shape.x();
  const lineY = shape.y();
  const scaleX = shape.scaleX();
  const scaleY = shape.scaleY();

  let pointsString = "";
  for (let i = 0; i < points.length; i += 2) {
    pointsString += `${getScaledPointCoord(points[i], scaleX, lineX)},${getScaledPointCoord(points[i + 1], scaleY, lineY)} `;
  }

  const elementType = shape.closed() ? "polygon" : "polyline";
  const svgElement = `<${elementType} id="${shape.id()}" ${transformStr} x="${shape.attrs.startX}" y="${shape.attrs.startY}" points="${pointsString.trim()}" fill="${fill}" stroke="${stroke}" stroke-width="${strokeWidth}" />`;

  return { svgElement };
}

/**
 * Converts a Konva Arrow to SVG path with marker
 */
function convertArrowToSVG(shape: Arrow, transformStr: string, attrs: CommonShapeAttributes): StageToSVGConversionResult {
  const svgElement = konvaArrowToSVG(shape, transformStr, attrs);
  return { svgElement };
}

// ======================
// Spotlight Utilities
// ======================

/**
 * Builds the spotlight mask definition
 */
function buildSpotlightMask(maskElements: string[], stage: Konva.Stage): string {
  if (!maskElements.length) return "";

  return `
  <defs>
    <mask id='spotlight-mask'>
      <rect skip="true" x="0" y="0" width="${stage.width()}" height="${stage.height()}" fill="white" />${maskElements.join("")}</mask></defs>`;
}

/**
 * Creates the spotlight background overlay
 */
function createSpotlightBackground(stage: Konva.Stage): string {
  return `<rect skip="true" x="0" y="0" width="${stage.width()}" height="${stage.height()}" fill="#000" mask="url(#spotlight-mask)" opacity=".8"/>`;
}

// ======================
// Main Function
// ======================

export function getSVGStringFromKonvaStage(stage: Konva.Stage): string {
  let shapesCount = 0;
  let svgString = `<svg width="${BASE_TELESTRATION_STAGE_SIZE.width}" height="${BASE_TELESTRATION_STAGE_SIZE.height}" xmlns="http://www.w3.org/2000/svg">`;
  const svgContentStartIndex = svgString.length;
  const maskElements: string[] = [];

  stage.getLayers().forEach((layer) => {
    const children = layer.getChildren();
    shapesCount += children.length;

    children.forEach((shape) => {
      if (shape instanceof Transformer) return;

      // Type guard to ensure we're working with a Shape, not a Group
      if (!("fill" in shape)) return;

      // Calculate transform string and common attributes once (shape-specific logic handled inside the functions)
      const transformStr = buildTransformString(shape);
      const commonAttrs = getCommonShapeAttributes(shape);

      let result: StageToSVGConversionResult;

      // Convert shape based on type using specific converters
      if (shape instanceof Path) {
        result = convertPathToSVG(shape, transformStr, commonAttrs);
      } else if (shape instanceof Ellipse) {
        result = convertEllipseToSVG(shape, transformStr, commonAttrs);
      } else if (shape instanceof Circle) {
        result = convertCircleToSVG(shape, transformStr, commonAttrs);
      } else if (shape instanceof Rect) {
        result = convertRectToSVG(shape, transformStr, commonAttrs);
      } else if (shape instanceof Text) {
        result = convertTextToSVG(shape);
      } else if (shape instanceof Arrow) {
        result = convertArrowToSVG(shape, transformStr, commonAttrs);
      } else if (shape instanceof Line) {
        result = convertLineToSVG(shape, transformStr, commonAttrs);
      } else {
        return; // Unknown shape type
      }

      // Add SVG element to main content
      if (result.svgElement) {
        svgString += result.svgElement;
      }

      // Collect mask elements for spotlight shapes
      if (result.maskElement) {
        maskElements.push(result.maskElement);
      }
    });
  });

  // Early return if not enough shapes
  if (shapesCount <= 2) return "";

  // Add spotlight mask if needed
  if (maskElements.length > 0) {
    const maskBg = createSpotlightBackground(stage);
    svgString = svgString.substring(0, svgContentStartIndex) + maskBg + svgString.substring(svgContentStartIndex);
    svgString += buildSpotlightMask(maskElements, stage);
  }

  svgString += "</svg>";
  return svgString;
}

function konvaArrowToSVG(arrow: Arrow, transformStr: string, { stroke, strokeWidth, dash }: CommonShapeAttributes): string {
  const id = arrow.id();
  const points = arrow.points();

  // Points are absolute coordinates relative to stage, not relative to arrow position
  // We need to subtract arrow position to get relative coordinates for SVG path
  const arrowX = arrow.x();
  const arrowY = arrow.y();
  const scaleX = arrow.scaleX();
  const scaleY = arrow.scaleY();

  let svgPath = `M${getScaledPointCoord(points[0], scaleX, arrowX)},${getScaledPointCoord(points[1], scaleY, arrowY)}`;
  for (let i = 2; i < points.length; i += 2) {
    svgPath += ` L${getScaledPointCoord(points[i], scaleX, arrowX)},${getScaledPointCoord(points[i + 1], scaleY, arrowY)}`;
  }

  // Compute arrowhead position and orientation using the last segment (pre base transform)
  let arrowheadElement = "";
  if (points.length >= 4) {
    const prevX = getScaledPointCoord(points[points.length - 4], scaleX, arrowX);
    const prevY = getScaledPointCoord(points[points.length - 3], scaleY, arrowY);
    const endX = getScaledPointCoord(points[points.length - 2], scaleX, arrowX);
    const endY = getScaledPointCoord(points[points.length - 1], scaleY, arrowY);

    const dx = endX - prevX;
    const dy = endY - prevY;
    const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
    // Compose base arrow transform with local arrowhead transforms.
    const baseOps = (transformStr || "").replace(/^transform="/, "").replace(/"$/, "");
    const combinedTransform = `${baseOps} translate(${endX}, ${endY}) rotate(${angleDeg})`;

    // Reuse template arrowhead, set stroke/color/width and combined transform
    arrowheadElement = `<path data-arrowhead="true" skip="true" transform="${combinedTransform}" d="${ARROWHEAD_TEMPLATE_PATH_D}" fill="none" stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linejoin="miter" stroke-linecap="butt" />`;
  }

  const shaftElement = `<path stroke-dasharray="${dash}" ${transformStr} id="${id}" stroke="${stroke}" stroke-width="${strokeWidth}" d="${svgPath}" fill="none" />`;

  return `${shaftElement}${arrowheadElement}`;
}
